import React, { useCallback, useMemo } from 'react';
import { useFilters, usePagination } from 'further-ui/hooks';
import {
  Archive,
  Unarchive,
  CheckCircle,
  Cancel,
  Visibility,
} from '@mui/icons-material';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { dateToLabel } from 'further-ui/utils';
import { FirmSelector } from 'components/FirmSelector';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import SearchField from 'components/SearchField';
import { TableActions } from 'components/Table';
import { InvestorLink } from 'components/Links';
import { KycProcessStatus, AmlResultsArchiveStatus } from 'further-types/kyc';
import { AmlMatchTypeLabels } from 'further-ui/labels';
import {
  formatAmlStatusLabel,
  formatTypeOfFailureString,
  formatCountryNamesString,
} from 'helpers/kyc/text-formatting';
import AmlResultInformation from 'routes/Pages/Investor/ViewInvestor/KycResults/AmlResultInformation';
import api, { ApiInputs, ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Column } from 'components/InteractiveTable';
import { useNotification } from 'hooks/ui/useNotification';
import {
  StyledFiltersContainer,
  StyledFilterField,
  TableContainer,
  StyledTable,
  StyledArchiveStatusSelect,
} from './styles';
import { SortOrder } from 'further-types/utils';

import ApprovalNoteCell from './ApprovalNoteCell';
import { Filters, Props } from './types';
import { createInvestorViewRoute } from 'adminConstants/routes';
import { useHistory } from 'react-router-dom';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type ListAmlRecordsResponse = ApiResponses['kyc']['listAmlRecords'];

const AmlTable: React.FC<Props> = ({ isSuperAdmin }) => {
  const { success } = useNotification();
  const history = useHistory();
  const utils = api.useUtils();
  const [canEditAml] = useHasPermissions([RolePermission.EditInvestorKyc]);

  const { filters, handleChangeFilters, isFiltersChangePending } =
    useFilters<Filters>('kyc-aml-filters', {
      defaultFilters: {
        searchTerm: '',
        dateRange: {},
      },
    });

  const pagination = usePagination({
    id: 'kyc-aml-records',
    orderBy: 'date',
    order: SortOrder.Desc,
    pageSize: 10,
    page: 1,
  });

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      handleChangeFilters({ searchTerm });
    },
    [handleChangeFilters],
  );

  const handleDateRangeChange = useCallback(
    (dateRange: Filters['dateRange']) => {
      handleChangeFilters({ dateRange });
    },
    [handleChangeFilters],
  );

  const handleFirmChange = useCallback(
    (firmId: string) => {
      handleChangeFilters({ firmId });
    },
    [handleChangeFilters],
  );

  const handleArchiveStatusChange = useCallback(
    (archiveStatus: string) => {
      handleChangeFilters({ archiveStatus });
    },
    [handleChangeFilters],
  );

  const { data: amlData, isFetching } = api.kyc.listAmlRecords.useQuery({
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
    searchTerm: filters.searchTerm ?? undefined,
    dateRange:
      filters.dateRange.startDate || filters.dateRange.endDate
        ? {
            startDate: filters.dateRange.startDate?.toISOString(),
            endDate: filters.dateRange.endDate?.toISOString(),
          }
        : undefined,
    firmId: filters.firmId,
    archiveStatus: filters.archiveStatus,
  });

  const createAmlStatusOptimisticUpdate = (newStatus: KycProcessStatus) => {
    return (
      oldData: ListAmlRecordsResponse | undefined,
      variables: ApiInputs['kyc']['updateAmlStatus'],
    ) => {
      if (!oldData?.result) return oldData;
      return {
        ...oldData,
        result: oldData.result.map((record) =>
          record.investorId === variables.investorId
            ? { ...record, status: newStatus }
            : record,
        ),
      };
    };
  };

  const createAmlApprovalNoteOptimisticUpdate = () => {
    return (
      oldData: ListAmlRecordsResponse | undefined,
      variables: ApiInputs['kyc']['updateAmlApprovalNote'],
    ) => {
      if (!oldData?.result) return oldData;
      return {
        ...oldData,
        result: oldData.result.map((record) =>
          record.investorId === variables.investorId
            ? { ...record, approvalNote: variables.approvalNote }
            : record,
        ),
      };
    };
  };

  const { mutate: updateAmlStatus } = api.kyc.updateAmlStatus.useMutation({
    onMutate: async (variables: ApiInputs['kyc']['updateAmlStatus']) => {
      const input = {
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        order: pagination.order,
        orderBy: pagination.orderBy,
        searchTerm: filters.searchTerm ?? undefined,
        dateRange:
          filters.dateRange.startDate || filters.dateRange.endDate
            ? {
                startDate: filters.dateRange.startDate?.toISOString(),
                endDate: filters.dateRange.endDate?.toISOString(),
              }
            : undefined,
        firmId: filters.firmId,
        archiveStatus: filters.archiveStatus,
      } as const;

      // Cancel any outgoing refetches for this specific query
      await utils.kyc.listAmlRecords.cancel(input);

      // Snapshot previous value
      const previousData = utils.kyc.listAmlRecords.getData(input);

      // Optimistically update
      const newStatus = variables.approve
        ? KycProcessStatus.AdminApproved
        : KycProcessStatus.Failed;
      const optimisticUpdate = createAmlStatusOptimisticUpdate(newStatus);
      utils.kyc.listAmlRecords.setData(input, (old) =>
        optimisticUpdate(old, variables),
      );

      return { previousData, input };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData && context?.input) {
        utils.kyc.listAmlRecords.setData(context.input, context.previousData);
      }
    },
    onSuccess: () => {
      success('AML status updated successfully.');
      utils.kyc.listAmlRecords.invalidate();
    },
  });

  const { mutate: updateAmlApprovalNote } =
    api.kyc.updateAmlApprovalNote.useMutation({
      onMutate: async (
        variables: ApiInputs['kyc']['updateAmlApprovalNote'],
      ) => {
        const input = {
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          order: pagination.order,
          orderBy: pagination.orderBy,
          searchTerm: filters.searchTerm ?? undefined,
          dateRange:
            filters.dateRange.startDate || filters.dateRange.endDate
              ? {
                  startDate: filters.dateRange.startDate?.toISOString(),
                  endDate: filters.dateRange.endDate?.toISOString(),
                }
              : undefined,
          firmId: filters.firmId,
          archiveStatus: filters.archiveStatus,
        } as const;

        // Cancel any outgoing refetches for this specific query
        await utils.kyc.listAmlRecords.cancel(input);

        // Snapshot previous value
        const previousData = utils.kyc.listAmlRecords.getData(input);

        // Optimistically update
        const optimisticUpdate = createAmlApprovalNoteOptimisticUpdate();
        utils.kyc.listAmlRecords.setData(input, (old) =>
          optimisticUpdate(old, variables),
        );

        return { previousData, input };
      },
      onError: (_error, _variables, context) => {
        if (context?.previousData && context?.input) {
          utils.kyc.listAmlRecords.setData(context.input, context.previousData);
        }
      },
      onSuccess: () => {
        success('Approval note updated successfully.');
      },
    });

  const { mutate: archiveAmlHit } = api.kyc.archiveAmlHit.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'AML hit archived successfully.',
    }),
  );

  const { mutate: unarchiveAmlHit } = api.kyc.unarchiveAmlHit.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'AML hit unarchived successfully.',
    }),
  );

  const handleApproveAml = (investorId: string) => {
    updateAmlStatus({
      investorId,
      approve: true,
      isKyc: false,
    });
  };

  const handleUnapproveAml = (investorId: string) => {
    updateAmlStatus({ investorId, approve: false, isKyc: false });
  };

  const handleArchiveAmlHit = (investorId: string, caDocId: string) => {
    archiveAmlHit({ investorId, caDocId });
  };

  const handleUnarchiveAmlHit = (investorId: string, caDocId: string) => {
    unarchiveAmlHit({ investorId, caDocId });
  };

  const handleApprovalNoteChange = (investorId: string, note: string) => {
    updateAmlApprovalNote({
      investorId,
      approvalNote: note,
    });
  };

  const formatReasonString = (reasons: Array<string>) => {
    return reasons
      .map((reason) => AmlMatchTypeLabels[reason] ?? reason)
      .join(', ');
  };

  const columns: Array<Column> = useMemo(
    () => [
      {
        field: 'date',
        minWidth: 90,
        headerName: 'Date',
        sortable: true,
        renderCell: ({ row }) => dateToLabel(row.date),
      },
      {
        field: 'investor',
        flex: 1,
        minWidth: 120,
        headerName: 'Investor',
        sortable: true,
        renderCell: ({ row }) => (
          <InvestorLink
            investorId={row.investorId}
            investorName={row.investorName}
          />
        ),
      },
      {
        field: 'hitName',
        flex: 1.3,
        minWidth: 120,
        headerName: 'Hit Name',
        sortable: false,
        renderCell: ({ row }) => row.hitName,
      },
      {
        field: 'type',
        flex: 1,
        minWidth: 120,
        headerName: 'Type',
        sortable: false,
        renderCell: ({ row }) =>
          row.type ? formatTypeOfFailureString(row.type) : '-',
      },
      {
        field: 'reason',
        flex: 1,
        minWidth: 120,
        headerName: 'Reason',
        sortable: false,
        renderCell: ({ row }) => formatReasonString(row.reason),
      },
      {
        field: 'countries',
        flex: 1,
        minWidth: 120,
        headerName: 'Countries',
        sortable: false,
        renderCell: ({ row }) => formatCountryNamesString(row.countries),
      },
      {
        field: 'status',
        width: 160,
        headerName: 'Status',
        sortable: true,
        renderCell: ({ row }) =>
          formatAmlStatusLabel(
            row.status,
            row.reason,
            row.countries,
            row.riskRating,
          ),
      },
      {
        field: 'approvalNote',
        flex: 2,
        minWidth: 200,
        headerName: 'Approval Note',
        sortable: false,
        renderCell: ({ row }) => (
          <ApprovalNoteCell
            value={row.approvalNote}
            onCommit={(content) =>
              handleApprovalNoteChange(row.investorId || '', content)
            }
          />
        ),
      },
      {
        field: 'actions',
        width: 80,
        headerName: 'Actions',
        sortable: false,
        renderCell: ({ row }) => {
          const actions: Array<{
            label: string;
            icon: OverridableComponent<SvgIconTypeMap<object>>;
            color: 'primary' | 'error' | 'default';
            onClick: () => void;
          }> = [];

          // Show "Approve AML" only if status is Consider (Failed)
          if (row.status === KycProcessStatus.Failed && canEditAml) {
            actions.push({
              label: 'Approve AML',
              icon: CheckCircle,
              color: 'primary',
              onClick: () => handleApproveAml(row.investorId || ''),
            });
          }

          // Show "Unapprove AML" if status is Pass or AdminApproved
          if (
            (row.status === KycProcessStatus.Passed ||
              row.status === KycProcessStatus.AdminApproved) &&
            canEditAml
          ) {
            actions.push({
              label: 'Unapprove AML',
              icon: Cancel,
              color: 'error',
              onClick: () => handleUnapproveAml(row.investorId || ''),
            });
          }

          // Add archive/unarchive actions
          if (row.archived && canEditAml) {
            actions.push({
              label: 'Unarchive',
              icon: Unarchive,
              color: 'primary',
              onClick: () =>
                handleUnarchiveAmlHit(row.investorId || '', row.caDocId || ''),
            });
          } else if (!row.archived && canEditAml) {
            actions.push({
              label: 'Archive',
              icon: Archive,
              color: 'error',
              onClick: () =>
                handleArchiveAmlHit(row.investorId || '', row.caDocId || ''),
            });
          }

          actions.push({
            label: 'View investor profile',
            icon: Visibility,
            color: 'primary',
            onClick: () =>
              history.push(createInvestorViewRoute(row.investorId || '')),
          });

          return (
            <TableActions stopPropagation showAsDropdown actions={actions} />
          );
        },
      },
    ],
    [
      handleApproveAml,
      handleUnapproveAml,
      handleApprovalNoteChange,
      handleArchiveAmlHit,
      handleUnarchiveAmlHit,
    ],
  );

  const rows = useMemo(() => {
    return (amlData?.result || []).map((record) => ({
      ...record,
      id: record.id,
      isExpandable: true,
    }));
  }, [amlData?.result]);

  return (
    <div>
      <StyledFiltersContainer>
        <StyledFilterField>
          <SearchField
            placeholder="Search by investor"
            value={filters.searchTerm}
            onSearch={handleSearchChange}
            allowEmptyQuery
            autoSearchOnDebounce
            fullWidth
          />
        </StyledFilterField>
        <StyledFilterField>
          <MuiDateRangePicker
            dateRange={filters.dateRange}
            onDateRangeChange={handleDateRangeChange}
            placeholder="Select date range"
          />
        </StyledFilterField>
        {isSuperAdmin && (
          <StyledFilterField>
            <FirmSelector
              firmId={filters.firmId}
              onChange={handleFirmChange}
              placeholder="Select firm"
            />
          </StyledFilterField>
        )}
        <StyledFilterField>
          <StyledArchiveStatusSelect
            options={[
              {
                label: 'Not archived',
                value: AmlResultsArchiveStatus.NotArchived,
              },
              {
                label: 'Archived',
                value: AmlResultsArchiveStatus.Archived,
              },
              {
                label: 'Show all',
                value: AmlResultsArchiveStatus.All,
              },
            ]}
            value={filters.archiveStatus ?? AmlResultsArchiveStatus.NotArchived}
            placeholder="Select archive status"
            onChange={(e: any) => {
              handleArchiveStatusChange(e.target.value);
            }}
          />
        </StyledFilterField>
      </StyledFiltersContainer>

      <TableContainer>
        <StyledTable
          id="kyc-aml-records"
          columns={columns}
          rows={rows}
          pagination={pagination}
          rowCount={amlData?.totalCount || 0}
          loading={isFetching || isFiltersChangePending}
          disableTableConfiguration
          disableSorting={false}
          fullHeight
          scrollable
          getRowHeight={() => 'auto'}
          getExpandedContent={(row) => (
            <AmlResultInformation
              caDocId={row.caDocId}
              investorId={row.investorId}
            />
          )}
        />
      </TableContainer>
    </div>
  );
};

export default AmlTable;
